// /crm/prospects/:id/discovery — Discovery Call notes form.
//
// Full-page form (NOT a modal) so accidental nav/click-off doesn't lose
// the content of an in-progress call. Auto-saves every change to
// prospect_meeting_notes via the useMeetingNotes hook.
//
// Mirrors the original HTML form's structure: 6 segments, ICP-filtered
// tracks, objection handler reference at the bottom.

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePipeline } from '../../context/PipelineContext';
import { useMeetingNotes } from './useMeetingNotes';
import { DISCOVERY_TRACKS, DISCOVERY_OBJECTIONS, ICP_LABELS } from './meetingData';
import MeetingFormLayout from './MeetingFormLayout';

const DEFAULT_FORM = {
  callDate: new Date().toISOString().slice(0, 10),
  qFamily: '',
  qIncome: '',
  qBusiness: '',
  qRealEstate: '',
  qRetirement: '',
  qAdvisors: '',
  qWhy: '',
  icpFilters: { business: false, 'rental-active': false, 'rental-passive': false, retiree: false },
  trackRun: {},        // { [trackId]: bool }
  trackNotes: {},      // { [trackId]: string }
  closeMeeting: '',
  closeSpouse: false,
  closeReturns: false,
  closeLinkSent: false,
  closeUploadSent: false,
  closeNotes: '',
};

export default function DiscoveryCallView() {
  const { id: prospectId } = useParams();
  const { prospects } = usePipeline();
  const prospect = useMemo(() => (prospects || []).find(p => p.id === prospectId), [prospects, prospectId]);

  const {
    formData, updateField, loaded, savedAt, saving, loadError, saveError,
  } = useMeetingNotes({ prospectId, type: 'discovery', defaultFormData: DEFAULT_FORM });

  // Filtered tracks based on selected ICP segments
  const visibleTracks = useMemo(() => {
    const selected = Object.entries(formData.icpFilters || {}).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) return [];
    const seen = new Set();
    return DISCOVERY_TRACKS
      .filter(t => t.icp.some(i => selected.includes(i)))
      .filter(t => seen.has(t.id) ? false : seen.add(t.id));
  }, [formData.icpFilters]);

  function toggleIcp(key) {
    updateField('icpFilters', { ...formData.icpFilters, [key]: !formData.icpFilters?.[key] });
  }
  function toggleTrackRun(id) {
    updateField('trackRun', { ...formData.trackRun, [id]: !formData.trackRun?.[id] });
  }
  function setTrackNote(id, value) {
    updateField('trackNotes', { ...formData.trackNotes, [id]: value });
  }

  return (
    <MeetingFormLayout
      title="Discovery Call"
      prospect={prospect}
      savedAt={savedAt}
      saving={saving}
      loadError={loadError}
      saveError={saveError}
    >
      {!loaded ? (
        <div className="text-[12px] text-[#888] p-4">Loading saved notes…</div>
      ) : (
        <>
          {/* Date */}
          <Card>
            <Label>Call date</Label>
            <input
              type="date"
              value={formData.callDate || ''}
              onChange={e => updateField('callDate', e.target.value)}
              className={inputCls + ' max-w-[180px]'}
            />
          </Card>

          {/* Segment 1 — Frame */}
          <Segment num="1" title="Frame the Meeting" duration="3 min">
            <Note>Open with control. Read this verbatim or in your own words. The structure is fixed: explain the format, set the agenda, state pricing comes later.</Note>
            <Script>
              "Before we get started, let me explain how I run these. We're going to spend most of our time understanding your situation, then I'm going to point out a few things you should be aware of. By the end you'll know whether we're a fit, and I'll have enough to tell you specifically what we'd do for you. We won't be going through pricing today. That comes in the next meeting if we both agree there's a fit. Sound good?"
            </Script>
          </Segment>

          {/* Segment 2 — Information Gathering */}
          <Segment num="2" title="Information Gathering" duration="10 min">
            <Note>Get what you need to run the right tracks. Ask the questions in order. Capture answers below.</Note>
            <div className="bg-gray-50 rounded-[4px] p-4 mb-3">
              <ol className="list-decimal ml-5 space-y-2 text-[15px] leading-[1.55]">
                <li>"Tell me about you and your family. Married, kids, where do you live?"</li>
                <li>"What does your income picture look like, including W2, business, rentals, anything else?"</li>
                <li>"Walk me through your business if you have one. Structure, revenue, employees, how long you've been at it."</li>
                <li>"Tell me about any real estate beyond your primary residence."</li>
                <li>"What does your retirement timeline look like? When do you want to stop working?"</li>
                <li>"Who are you currently working with on this? CPA, financial advisor, attorney, anyone else?"</li>
                <li><strong>"What made you reach out to us specifically?"</strong> (the goal question, calibrate tracks based on this answer)</li>
              </ol>
            </div>
            <Field label="Family: married, kids, location"     value={formData.qFamily}     onChange={v => updateField('qFamily', v)} />
            <Field label="Income picture"                       value={formData.qIncome}     onChange={v => updateField('qIncome', v)} />
            <Field label="Business: structure, revenue, employees, tenure" value={formData.qBusiness} onChange={v => updateField('qBusiness', v)} />
            <Field label="Real estate: properties, types, hold timeline" value={formData.qRealEstate} onChange={v => updateField('qRealEstate', v)} />
            <Field label="Retirement timeline"                  value={formData.qRetirement} onChange={v => updateField('qRetirement', v)} />
            <Field label="Current professional relationships"   value={formData.qAdvisors}   onChange={v => updateField('qAdvisors', v)} />
            <Field label="Why they reached out (the pain that drove the call)" value={formData.qWhy} onChange={v => updateField('qWhy', v)} />
          </Segment>

          {/* Segment 3 — Tracks */}
          <Segment num="3" title="Run the Tracks" duration="15-20 min">
            <Note>Pick 2 to 3 tracks calibrated to what they told you. Check the ICP segment(s) below to filter the relevant tracks.</Note>
            <div className="bg-yellow-50 border border-yellow-200 rounded-[4px] p-3 mb-3 text-[12px]">
              <strong className="block mb-1">Two non-negotiables:</strong>
              1. Ask the question, then stop talking. Wait. The silence is the disturbance.<br />
              2. After they answer, deliver the math. Then ask: "Has anyone ever walked you through this?" Wait again.
            </div>
            <div className="bg-bone/40 rounded-[4px] p-3 mb-3">
              <div className="text-[11px] font-bold mb-2">ICP Segment(s):</div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(ICP_LABELS).map(([key, label]) => (
                  <label key={key} className="text-[12px] flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={!!formData.icpFilters?.[key]}
                      onChange={() => toggleIcp(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {visibleTracks.length === 0 ? (
              <div className="text-[12px] text-[#888] italic text-center py-4">
                Select one or more ICP segments above to view relevant tracks.
              </div>
            ) : (
              <div className="space-y-3">
                {visibleTracks.map(track => (
                  <div key={track.id} className="border border-gray-200 rounded-[4px] p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={!!formData.trackRun?.[track.id]}
                        onChange={() => toggleTrackRun(track.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-bold text-[13px] text-navy">{track.title}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {track.icp.map(i => (
                            <span key={i} className="text-[9px] bg-bone/60 text-navy px-1.5 py-0.5 rounded-full">
                              {ICP_LABELS[i]}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-[3px] p-3.5 text-[16px] italic leading-[1.65] mb-2 whitespace-pre-wrap">
                      {track.script}
                    </div>
                    <Field
                      label="What did they say? Where did the disturbance land?"
                      value={formData.trackNotes?.[track.id] || ''}
                      onChange={v => setTrackNote(track.id, v)}
                    />
                  </div>
                ))}
              </div>
            )}
          </Segment>

          {/* Segment 4 — Transition */}
          <Segment num="4" title="Transition to Wealth Management Frame" duration="5 min">
            <Note>After 2 to 3 tracks, transition out of tax pain and into wealth management positioning. Sell the relationship, not the deliverable. We're a wealth management firm. Tax planning is how we lead, not the whole engagement.</Note>
            <SubBold>Path A: Prospect has tax plan opportunity (most common)</SubBold>
            <Script>
              "Here's what we just did. We surfaced [X], [Y], and [Z]. Each of those is a real dollar number sitting on your tax return. But that's not why you'd hire us. You'd hire us because every one of those issues touches something else, your retirement plan, your estate, your investments, your insurance, your kids. Tax planning is how we lead, but it's not the whole engagement. We're a wealth management firm. We coordinate everything. We'd build that as either our Bullogic Guided Wealth package or our Bullogic Total Wealth package, depending on which fits your situation. Either way, it's a relationship where one person is finally watching the whole picture for you, instead of you trying to coordinate four separate professionals who don't talk to each other."
            </Script>
            <SubBold>Path B: Prospect fits ICP but no real tax plan opportunity</SubBold>
            <Script>
              "Here's what I see for you. [X] and [Y] aren't tax problems, they're planning problems. They don't get solved with a one-time deliverable. They get solved by having someone watching the whole picture for you year-round. That's what Bullogic Guided Wealth is built for.
              {'\n\n'}
              Throughout the year, we run quarterly tax estimates so you're never surprised at filing. Two formal planning meetings to keep your net worth, cash flow, and goals on track. A tax authority review every year to confirm nothing's sitting at the IRS you don't know about. Roth conversion analysis if those apply. An annual tax planning meeting in Q4 to set up the next year. A supplemental review that alternates between insurance coverage and estate plan, every year. Plus continuous monitoring and we're available when something changes.
              {'\n\n'}
              That's roughly 15 specific touchpoints across the year. We have an Annual Service Calendar that lays it all out, I'll send it to you right after this call. Most advisors charge a one-time fee for a financial plan and then disappear. We don't disappear."
            </Script>
            <Note><strong>Tactical:</strong> The Annual Service Calendar PDF is your strongest BGW asset. Send it as a leave-behind with the upload-link email after every Path B Discovery.</Note>
          </Segment>

          {/* Segment 5 — Close */}
          <Segment num="5" title="Close to Strategy Meeting" duration="5 min">
            <Note>Three commitments before they leave the call. Don't accept "I'll think about it" without addressing it directly.</Note>
            <SubBold>1. The direct ask:</SubBold>
            <Script>"Are you ready to move forward and book the Strategy Meeting?"</Script>
            <SubBold>2. Spouse on the call (non-negotiable for couples):</SubBold>
            <Script>"For the Strategy Meeting, I need both of you on the call. The decisions we'll be making affect both of you, and the meetings work better when we hear from both. Can we find a time that works for both of you?"</Script>
            <SubBold>3. Documents requested:</SubBold>
            <Script>"I need your last year's tax return and any current year P&L if you have a business. I'll send you a secure upload link after this call. Once those are in, I'll do the analysis and we'll meet."</Script>
            <Label>Strategy Meeting booked? Date / time</Label>
            <input
              type="text"
              value={formData.closeMeeting || ''}
              onChange={e => updateField('closeMeeting', e.target.value)}
              placeholder="e.g., Tuesday May 14, 2 PM"
              className={inputCls}
            />
            <div className="space-y-1.5 mt-3">
              {[
                ['closeSpouse', 'Spouse confirmed for Strategy Meeting'],
                ['closeReturns', 'Tax returns and P&L requested'],
                ['closeLinkSent', 'Calendar link sent'],
                ['closeUploadSent', 'Upload link sent'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-[12px]">
                  <input type="checkbox" checked={!!formData[key]} onChange={() => updateField(key, !formData[key])} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <Field
              label="Other notes, commitments, red flags, follow-up items"
              value={formData.closeNotes}
              onChange={v => updateField('closeNotes', v)}
              minHeight={120}
            />
          </Segment>

          {/* Segment 6 — Objection Handlers */}
          <Segment num="6" title="Objection Handlers" duration="reference">
            <Note>Quick reference for objections that come up during Discovery. Click to expand.</Note>
            {DISCOVERY_OBJECTIONS.map((obj, i) => (
              <details key={i} className="border border-gray-200 rounded-[4px] mb-2 group">
                <summary className="cursor-pointer p-3 text-[13px] font-bold list-none flex items-center gap-2 hover:bg-bone/30">
                  <span className="text-bullogic-blue text-[14px] group-open:rotate-45 transition-transform">+</span>
                  <span>{obj.title}</span>
                </summary>
                <div className="p-3 pt-1 border-t border-gray-100">
                  <div className="text-[11px] text-[#777] italic mb-2">When it comes: {obj.when}</div>
                  <div className="text-[12px] mb-2 p-2 bg-gray-50 rounded border-l-2 border-gray-400">
                    <strong>Principle:</strong> {obj.principle}
                  </div>
                  <div className="bg-bone/40 border-l-[3px] border-bullogic-blue p-4 italic text-[16px] whitespace-pre-wrap leading-[1.65]">
                    {obj.script}
                  </div>
                </div>
              </details>
            ))}
          </Segment>
        </>
      )}
    </MeetingFormLayout>
  );
}

// ─── Reusable bits ───────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue';

function Card({ children }) {
  return <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">{children}</div>;
}
function Segment({ num, title, duration, children }) {
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-navy text-white text-[11px] font-bold">{num}</span>
        <h2 className="text-[15px] font-bold text-navy flex-1">{title}</h2>
        {duration && <span className="text-[10px] text-[#888]">{duration}</span>}
      </div>
      {children}
    </div>
  );
}
function Note({ children }) { return <p className="text-[12px] text-[#666] mb-2">{children}</p>; }
function SubBold({ children }) { return <div className="text-[12px] font-bold text-navy mt-3 mb-1">{children}</div>; }
function Script({ children }) {
  // Scripts are read live during calls — larger font + relaxed leading
  // makes them scannable without leaning toward the screen.
  return (
    <div className="bg-bone/40 border-l-[3px] border-bullogic-blue p-4 my-2 italic text-[16px] whitespace-pre-wrap leading-[1.65] rounded-r-[3px]">
      {children}
    </div>
  );
}
function Label({ children }) { return <label className="block text-[11px] font-bold uppercase tracking-wider text-navy mt-3 mb-1">{children}</label>; }
function Field({ label, value, onChange, minHeight = 56 }) {
  return (
    <>
      <Label>{label}</Label>
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className={inputCls}
        style={{ minHeight, resize: 'vertical' }}
      />
    </>
  );
}
